import { replaceUrlWithoutRoutePublish } from "../../../../../../_shared/_logic/navigation-silent-url-replace"
import type {
  CommunicationPlanChannelGroup,
  CommunicationPlanFilterValue,
  CommunicationPlanStatusGroup,
} from "../../../_logic/communication-plan-table-contract"
import {
  COMMUNICATION_PLAN_CHANNEL_OPTIONS,
  COMMUNICATION_PLAN_DEFAULT_STATUS_GROUPS,
  COMMUNICATION_PLAN_STATUS_OPTIONS,
  createDefaultCommunicationPlanFilters,
  formatCommunicationPlanDate,
  normalizeCommunicationPlanFilterValue,
} from "../_shared/_logic/filters"

const normalizeId = (value: string | null | undefined) =>
  (value ?? "kehrblatt-case-communications").trim().replace(/[^a-zA-Z0-9_-]+/g, "_") ||
  "kehrblatt-case-communications"

export const communicationPlanUrlStateId = (organizationId: string | null | undefined) =>
  normalizeId(
    organizationId
      ? `kehrblatt-case-communications-${organizationId}`
      : "kehrblatt-case-communications",
  )

const key = (stateId: string, name: string) => `table_${stateId}_filter_${name}`

const parseJsonStringArray = (value: string | null): string[] => {
  if (!value) return []

  try {
    const parsed: unknown = JSON.parse(value)

    if (Array.isArray(parsed)) {
      return parsed.map((entry) => String(entry ?? "").trim()).filter(Boolean)
    }
  } catch {
    // Keep supporting comma-separated URLs produced before JSON-array persistence.
  }

  return value
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean)
}

const parseDate = (value: string | null): Date | null => {
  if (!value) return null

  const parsed = new Date(`${value}T12:00:00`)

  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const setString = (
  params: URLSearchParams,
  stateId: string,
  name: string,
  value: string | null,
) => {
  const normalized = String(value ?? "").trim()

  if (normalized) params.set(key(stateId, name), normalized)
  else params.delete(key(stateId, name))
}

const setArray = (params: URLSearchParams, stateId: string, name: string, value: string[]) => {
  const normalized = Array.from(new Set(value.map((entry) => entry.trim()).filter(Boolean))).sort()

  if (normalized.length) params.set(key(stateId, name), JSON.stringify(normalized))
  else params.delete(key(stateId, name))
}

const replaceUrl = (params: URLSearchParams) => {
  if (typeof window === "undefined") return

  const search = params.toString()
  const nextUrl = `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash ?? ""}`
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash ?? ""}`

  if (nextUrl !== currentUrl) replaceUrlWithoutRoutePublish(nextUrl)
}

export const readCommunicationPlanUrlState = (
  organizationId: string | null | undefined,
): CommunicationPlanFilterValue => {
  const defaults = createDefaultCommunicationPlanFilters()

  if (typeof window === "undefined") return defaults

  const stateId = communicationPlanUrlStateId(organizationId)
  const params = new URLSearchParams(window.location.search || "")
  const allowedGroups = new Set(COMMUNICATION_PLAN_CHANNEL_OPTIONS.map((option) => option.value))
  const channelGroups = parseJsonStringArray(params.get(key(stateId, "channels"))).filter(
    (value): value is CommunicationPlanChannelGroup =>
      allowedGroups.has(value as CommunicationPlanChannelGroup),
  )
  const statusParamKey = key(stateId, "statuses")
  const allowedStatuses = new Set(COMMUNICATION_PLAN_STATUS_OPTIONS.map((option) => option.value))
  const statusGroups = (
    params.has(statusParamKey)
      ? parseJsonStringArray(params.get(statusParamKey))
      : COMMUNICATION_PLAN_DEFAULT_STATUS_GROUPS
  ).filter((value): value is CommunicationPlanStatusGroup =>
    allowedStatuses.has(value as CommunicationPlanStatusGroup),
  )

  return normalizeCommunicationPlanFilterValue({
    searchQuery: params.get(key(stateId, "search")) ?? "",
    appointmentDateRange: {
      start: parseDate(params.get(key(stateId, "appointment_start"))),
      end: parseDate(params.get(key(stateId, "appointment_end"))),
    },
    employeeIds: parseJsonStringArray(params.get(key(stateId, "employees"))),
    channelGroups,
    statusGroups,
  })
}

export const writeCommunicationPlanUrlState = (
  organizationId: string | null | undefined,
  value: CommunicationPlanFilterValue,
) => {
  if (typeof window === "undefined") return

  const stateId = communicationPlanUrlStateId(organizationId)
  const params = new URLSearchParams(window.location.search || "")
  const normalized = normalizeCommunicationPlanFilterValue(value)
  setString(params, stateId, "search", normalized.searchQuery)
  setString(
    params,
    stateId,
    "appointment_start",
    formatCommunicationPlanDate(normalized.appointmentDateRange.start),
  )
  setString(
    params,
    stateId,
    "appointment_end",
    formatCommunicationPlanDate(normalized.appointmentDateRange.end),
  )
  setArray(params, stateId, "employees", normalized.employeeIds)
  setArray(params, stateId, "channels", normalized.channelGroups)
  const normalizedDefaultStatuses = [...COMMUNICATION_PLAN_DEFAULT_STATUS_GROUPS].sort()
  const normalizedStatuses = [...normalized.statusGroups].sort()

  if (JSON.stringify(normalizedStatuses) === JSON.stringify(normalizedDefaultStatuses)) {
    params.delete(key(stateId, "statuses"))
  } else {
    params.set(key(stateId, "statuses"), JSON.stringify(normalizedStatuses))
  }

  replaceUrl(params)
}
